import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Switch } from 'antd';
import { MessageOutlined, RobotOutlined, WarningOutlined } from '@ant-design/icons';

import { TabsTruot } from '../../../../components/shared';
import { Badge, Button } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { useLuuBot } from '../../hooks/useBot';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import {
  TABS_BOT,
  giongNhau,
  loiCuaTab,
  tabChuaLuu,
  viecConThieu,
  type TabBot,
} from '../../nghiep-vu/cau-hinh-bot';
import { BOT_MAC_DINH, chiPhanGuiLen, type Chatbot, type ChatbotInput } from '../../types';
import { KHOI, type LuaChon } from './chung';
import { TabGiaoTiep } from './TabGiaoTiep';
import { TabHanhVi } from './TabHanhVi';
import { TabNangCao } from './TabNangCao';
import { TabNhung } from './TabNhung';
import { TabQuyen } from './TabQuyen';
import { TabTongQuan } from './TabTongQuan';
import { TabTriThuc } from './TabTriThuc';

function NhanTab({ ten, loi, ban }: { ten: string; loi: boolean; ban: boolean }) {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-1.5">
      {ten}
      {loi || ban ? (
        <>
          <span aria-hidden className={cn('size-1.5 rounded-full', loi ? 'bg-danger-500' : 'bg-warning-500')} />
          <span className="sr-only">{loi ? t('troLyAiBot.tabCanSua') : t('troLyAiBot.tabChuaLuu')}</span>
        </>
      ) : null}
    </span>
  );
}

/**
 * Phần đầu: bot là ai, đang chạy không, bật ở đâu, thử ở đâu — thấy được từ mọi tab. Việc còn thiếu là NÚT dẫn tới đúng
 * tab: "chưa gắn nguồn" mà không chỉ chỗ gắn thì người đọc phải mở từng tab đi tìm.
 */
function DauForm({
  f, bot, suaDuoc, onBat, onDenTab,
}: {
  f: ChatbotInput;
  bot: Chatbot | null;
  suaDuoc: boolean;
  onBat: (v: boolean) => void;
  onDenTab: (k: TabBot) => void;
}) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const thieu = viecConThieu(f).map((m) =>
    m === 'datTen'
      ? { ma: m, chu: t('troLyAiBot.viecDatTen'), tab: 'tongQuan' as const }
      : { ma: m, chu: t('troLyAiBot.viecGanNguon'), tab: 'triThuc' as const },
  );
  // Nền tảng tự tắt bot khi nguồn cuối cùng bị xoá, không qua request nào của màn này.
  const botTuTat = !!bot && !bot.active && !bot.answerable;

  return (
    <div className="space-y-3 border-b border-default px-5 pb-3 pt-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="rounded bg-primary-50 p-2 text-primary-700" aria-hidden>
          <RobotOutlined />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-content-primary">
            {f.name.trim() || t('troLyAiBot.chatbotMoi')}
          </h2>
          {bot ? (
            bot.active && bot.answerable ? (
              <Badge variant="success">{t('troLyAiBot.dangChay')}</Badge>
            ) : (
              <Badge variant="neutral">{t('troLyAiBot.dangTat')}</Badge>
            )
          ) : (
            <Badge variant="neutral">{t('troLyAiBot.chuaTao')}</Badge>
          )}
        </div>
        <label className="flex items-center gap-2 text-xs text-content-secondary">
          <Switch
            size="small"
            checked={f.active}
            disabled={!suaDuoc || (thieu.length > 0 && !f.active)}
            onChange={onBat}
          />
          {f.active ? t('troLyAiBot.dangBat') : t('troLyAiBot.dangTat')}
        </label>
        {/* Bot chưa trả lời được thì không có lối vào: nó sẽ mở ra một cuộc trò chuyện câm. */}
        {bot?.answerable ? (
          <Link to={duongDan(`/tro-ly-ai/tro-chuyen/${encodeURIComponent(bot.id)}`)}>
            <Button size="sm" variant="secondary" icon={<MessageOutlined />}>
              {t('troLyAiBot.thuTroChuyen')}
            </Button>
          </Link>
        ) : null}
      </div>

      {thieu.length ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded bg-warning-50 px-3 py-2 text-xs text-warning-700">
          <WarningOutlined />
          <span className="font-medium">{t('troLyAiBot.conViec', { n: soVN(thieu.length, locale) })}:</span>
          {thieu.map((m) => (
            <button
              key={m.ma}
              type="button"
              onClick={() => onDenTab(m.tab)}
              className="rounded underline underline-offset-2 hover:no-underline"
            >
              {m.chu}
            </button>
          ))}
        </div>
      ) : botTuTat ? (
        <p className="flex items-start gap-1.5 rounded bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
          <WarningOutlined className="mt-px shrink-0 text-xs" />
          <span>
            <b>{t('troLyAiBot.botTuTatDam')}</b> {t('troLyAiBot.botTuTatSau')}
          </span>
        </p>
      ) : null}
    </div>
  );
}

export function FormBot({
  bot, boTriThuc, nguonDuLieu, suaDuoc, onXong, onHuy, onXoa, onBan,
}: {
  bot: Chatbot | null;
  boTriThuc: LuaChon[];
  nguonDuLieu: LuaChon[];
  suaDuoc: boolean;
  onXong: (b: Chatbot) => void;
  onHuy: () => void;
  onXoa?: () => void;
  /** Báo lên màn cha để nó hỏi trước khi chuyển sang bot khác và làm mất bản đang sửa. */
  onBan: (ban: boolean) => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const goc = useMemo(() => (bot ? chiPhanGuiLen(bot) : BOT_MAC_DINH), [bot]);
  /*
   * Bản nháp gắn với MỐC `updatedAt` của bản máy chủ lúc bắt đầu sửa. Bản máy chủ đổi (lưu xong rồi tải lại) thì mốc lệch
   * và form tự theo bản mới — không cần effect chép dữ liệu, và không nhấp nháy về bản cũ trong lúc chờ tải lại.
   */
  const moc = bot?.updatedAt ?? '';
  const [nhap, setNhap] = useState<{ moc: string; du: ChatbotInput } | null>(null);
  const f = nhap && nhap.moc === moc ? nhap.du : goc;
  const [tab, setTab] = useState<TabBot>('tongQuan');
  // `TabsTruot` không nhận `activeKey`: nhảy tab từ dòng "còn việc" thì dựng lại hàng thẻ bằng `key`.
  const [lanNhay, setLanNhay] = useState(0);
  const luu = useLuuBot();

  const dat = <K extends keyof ChatbotInput>(k: K, v: ChatbotInput[K]) =>
    setNhap((cu) => ({ moc, du: { ...(cu && cu.moc === moc ? cu.du : goc), [k]: v } }));

  const coThayDoi = !bot || !giongNhau(f, goc);
  const loi = loiCuaTab(f);
  const chuaLuu = bot ? tabChuaLuu(f, goc) : [];

  // Báo trạng thái "đang sửa dở" lên màn cha — màn cha giữ nó trong ref, không vẽ lại theo.
  const ban = !!bot && !giongNhau(f, goc);
  useEffect(() => {
    onBan(ban);
  }, [ban, onBan]);
  useEffect(() => () => onBan(false), [onBan]);

  const denTab = (k: TabBot) => {
    setTab(k);
    setLanNhay((n) => n + 1);
  };

  const tp = { f, dat, suaDuoc };
  const noiDung: Record<TabBot, ReactNode> = {
    tongQuan: <TabTongQuan {...tp} />,
    triThuc: <TabTriThuc {...tp} boTriThuc={boTriThuc} nguonDuLieu={nguonDuLieu} />,
    hanhVi: <TabHanhVi {...tp} />,
    giaoTiep: <TabGiaoTiep {...tp} />,
    quyen: <TabQuyen botId={bot?.id ?? null} suaDuoc={suaDuoc} />,
    nhung: <TabNhung {...tp} nguonDuLieu={nguonDuLieu} botId={bot?.id ?? null} />,
    nangCao: <TabNangCao {...tp} onXoa={onXoa} />,
  };

  return (
    <div className={cn(KHOI, 'min-w-0')}>
      <DauForm f={f} bot={bot} suaDuoc={suaDuoc} onBat={(v) => dat('active', v)} onDenTab={denTab} />

      <div className="px-5 pb-5 pt-3">
        <TabsTruot
          key={lanNhay}
          gon
          defaultActiveKey={tab}
          onChange={(k) => setTab(k as TabBot)}
          items={TABS_BOT.map((k) => ({
            key: k,
            label: <NhanTab ten={t(`troLyAiBot.tab.${k}`)} loi={!!loi[k]} ban={chuaLuu.includes(k)} />,
            children: <div className="pt-3">{noiDung[k]}</div>,
          }))}
        />
      </div>

      {suaDuoc ? (
        // `sticky` chứ không `fixed`: chỉ ghim trong lúc biểu mẫu còn trong tầm nhìn.
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 rounded-b-lg border-t border-default bg-surface px-5 py-3">
          <p aria-live="polite" className={cn('text-xs', coThayDoi ? 'text-warning-700' : 'text-content-muted')}>
            {!bot
              ? t('troLyAiBot.botChuaTao')
              : coThayDoi
                ? t('troLyAiBot.coThayDoi', { n: soVN(chuaLuu.length || 1, locale) })
                : t('troLyAiBot.daLuuHet')}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={!!bot && !coThayDoi}
              onClick={() => (bot ? setNhap(null) : onHuy())}
            >
              {bot ? t('troLyAiBot.hoanTac') : t('chung.huy')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!f.name.trim() || !coThayDoi}
              loading={luu.isPending}
              onClick={() =>
                luu.mutate(
                  { id: bot?.id ?? null, du: f },
                  {
                    onSuccess: (b) => {
                      toast.success(bot ? t('troLyAiBot.daLuuCauHinh') : t('troLyAiBot.daTaoChatbot'));
                      onXong(b);
                    },
                    onError: (e) => toast.error(moTaLoi(e)),
                  },
                )
              }
            >
              {bot ? t('chung.luu') : t('troLyAiBot.taoChatbot')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
